import { ComponentProps, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import Link from '@alltrails/denali/components/Link';
import InfoFilled from '@alltrails/denali/icons/InfoFilled';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import { Modal, ModalStep, MultiStepModal, useMultiStepModal } from '@alltrails/modal';
import type Context from '@alltrails/types/Context';
import useActivityFilterConfig from '../../hooks/filterConfigs/useActivityFilterConfig';
import useDifficultyFilterConfig from '../../hooks/filterConfigs/useDifficultyFilterConfig';
import useDistanceAwayFilterConfig from '../../hooks/filterConfigs/useDistanceAwayFilterConfig';
import useDurationFilterConfig from '../../hooks/filterConfigs/useDurationFilterConfig';
import useElevationGainFilterConfig from '../../hooks/filterConfigs/useElevationGainFilterConfig';
import useFeatureFilterConfig from '../../hooks/filterConfigs/useFeatureFilterConfig';
import useHighestPointFilterConfig from '../../hooks/filterConfigs/useHighestPointFilterConfig';
import useLengthFilterConfig from '../../hooks/filterConfigs/useLengthFilterConfig';
import useMinRatingFilterConfig from '../../hooks/filterConfigs/useMinRatingFilterConfig';
import useRouteTypeFilterConfig from '../../hooks/filterConfigs/useRouteTypeFilterConfig';
import useSortDropdownFilterConfig from '../../hooks/filterConfigs/useSortDropdownFilterConfig';
import useSuitabilityFilterConfig from '../../hooks/filterConfigs/useSuitabilityFilterConfig';
import useTrailEngagementFilterConfig from '../../hooks/filterConfigs/useTrailEngagementFilterConfig';
import useTrailTrafficFilterConfig from '../../hooks/filterConfigs/useTrailTrafficFilterConfig';
import useDistanceAwayFilter from '../../hooks/useDistanceAwayFilter';
import useExploreServiceFilterCounts from '../../hooks/useExploreServiceFilterCounts';
import useExploreServiceFilterState from '../../hooks/useExploreServiceFilterState';
import { useIsSeasonalSort } from '../../hooks/useIsSeasonalSort';
import { ExploreServiceState, updateCustomSortValue, updateNaturalLanguageQuery } from '../../redux/exploreServiceSlice';
import { defaultExploreServiceSort } from '../../utils/constants';
import { getCurrentMonth } from '../../utils/seasonalMonth';
import DifficultyInfoModalContent from '../DifficultyInfoModalContent/DifficultyInfoModalContent';
import ExploreSeasonalMonthSelector from '../ExploreSeasonalMonthSelector/ExploreSeasonalMonthSelector';
import WheelchairInfoModalContent from '../WheelchairInfoModalContent/WheelchairInfoModalContent';
import styles from './AllFiltersModal.module.scss';
import FiltersModal from './FiltersModal/FiltersModal';

export const formatRatingValue = (rating: number) => {
  if (rating === 0) {
    return <FormattedMessage defaultMessage="Any" />;
  }
  return (
    <div className={styles.ratingValue}>
      <StarFilled color="--color-text-primary" size="xs" />
      <span>
        {rating}
        {rating === 5 ? null : '+'}
      </span>
    </div>
  );
};

type AllFiltersModalProps = Pick<ComponentProps<typeof Modal>, 'isOpen' | 'onCloseRequest'> & {
  map?: MapRef;
  onSeeTrailsClick?: () => void;
};

const AllFiltersModal = ({ isOpen, map, onCloseRequest, onSeeTrailsClick }: AllFiltersModalProps) => {
  const user = useUser();
  const dispatch = useDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const { activeModal, goBack, navigateTo } = useMultiStepModal<'initial' | 'difficulty' | 'wheelchair'>('initial');
  const { exploreServiceFilters, exploreServiceSort, onFiltersChange, onSortChange, onTrailEngagementChanged, trailEngagement } =
    useExploreServiceFilterState();

  const { filterCount } = useExploreServiceFilterCounts();
  const { isLoading, customSortOptions, customSortValue, searchResultsSummary, seasonalMonth, useMetric } = useSelector(
    (state: { context: Context; exploreService: ExploreServiceState }) => ({
      isLoading: state.exploreService.searchIsLoading,
      customSortOptions: state.exploreService.customSortOptions,
      customSortValue: state.exploreService.customSortValue,
      searchResultsSummary: state.exploreService.searchResultsSummary,
      seasonalMonth: state.exploreService.seasonalMonth || getCurrentMonth(),
      useMetric: state.context.displayMetric
    })
  );

  const {
    applyDistanceAway,
    distanceAway,
    distanceAwaySliderSubHeader,
    distanceAwaySignUpModal,
    distanceAwayUpsellModal,
    isDistanceAwaySignUpModalOpen,
    isDistanceAwayUpsellModalOpen,
    localDistanceAway,
    setLocalDistanceAway
  } = useDistanceAwayFilter(map);

  const distanceAwayFilter = useDistanceAwayFilterConfig({
    appliedDistanceAway: distanceAway?.distance,
    distanceAway: localDistanceAway,
    onDistanceAwayChange: setLocalDistanceAway,
    onDistanceAwayChangeEnd: applyDistanceAway,
    subHeader: distanceAwaySliderSubHeader,
    useMetric
  });

  const activityFilter = useActivityFilterConfig(exploreServiceFilters, onFiltersChange);

  const difficultyLink = useMemo(
    () => (
      <Link
        icon={{ Component: InfoFilled }}
        noUnderline
        onClick={() => {
          navigateTo('difficulty');
        }}
        size="sm"
        variant="secondary"
      >
        <FormattedMessage defaultMessage="About difficulty" />
      </Link>
    ),
    [navigateTo]
  );
  const difficultyFilter = useDifficultyFilterConfig(exploreServiceFilters, onFiltersChange, difficultyLink);

  const lengthFilter = useLengthFilterConfig(exploreServiceFilters, onFiltersChange, useMetric);
  const elevationGainFilter = useElevationGainFilterConfig(exploreServiceFilters, onFiltersChange, useMetric);
  const durationFilter = useDurationFilterConfig(exploreServiceFilters, onFiltersChange);

  const wheelchairLink = useMemo(
    () => (
      <Link
        noUnderline
        onClick={() => {
          navigateTo('wheelchair');
        }}
        size="sm"
        variant="secondary"
      >
        <FormattedMessage defaultMessage="Learn more" />
      </Link>
    ),
    [navigateTo]
  );
  const suitabilityFilter = useSuitabilityFilterConfig(exploreServiceFilters, onFiltersChange, wheelchairLink);

  const featureFilter = useFeatureFilterConfig(exploreServiceFilters, onFiltersChange);
  const routeTypeFilter = useRouteTypeFilterConfig(exploreServiceFilters, onFiltersChange);
  const minRatingFilter = useMinRatingFilterConfig(exploreServiceFilters, onFiltersChange, formatRatingValue);
  const trailTrafficFilter = useTrailTrafficFilterConfig(exploreServiceFilters, onFiltersChange);
  const highestPointFilter = useHighestPointFilterConfig(exploreServiceFilters, onFiltersChange, useMetric);
  const trailEngagementFilter = useTrailEngagementFilterConfig(trailEngagement, onTrailEngagementChanged);

  const isSeasonalSort = useIsSeasonalSort();

  const onCustomSortChange = useCallback(
    (updatedSort: string) => {
      dispatch(updateCustomSortValue(updatedSort));
    },
    [dispatch]
  );

  const sortFilter = useSortDropdownFilterConfig({
    additionalContent: !customSortOptions && isSeasonalSort ? <ExploreSeasonalMonthSelector selectedMonth={seasonalMonth} /> : undefined,
    customSortOptions,
    customSortValue,
    exploreServiceSort,
    onCustomSortChange,
    onSortChange
  });

  const filterConfigs = [
    sortFilter,
    distanceAwayFilter,
    activityFilter,
    difficultyFilter,
    lengthFilter,
    elevationGainFilter,
    durationFilter,
    suitabilityFilter,
    featureFilter,
    routeTypeFilter,
    minRatingFilter,
    trailTrafficFilter,
    highestPointFilter,
    ...(user ? [trailEngagementFilter] : [])
  ];

  return (
    <>
      <MultiStepModal
        isOpen={isOpen}
        isPaused={isDistanceAwaySignUpModalOpen || isDistanceAwayUpsellModalOpen}
        onBackRequest={goBack}
        onCloseComplete={() => {
          navigateTo('initial');
        }}
        onCloseRequest={onCloseRequest}
      >
        {activeModal === 'initial' && (
          <FiltersModal
            onCloseRequest={onCloseRequest}
            onSeeTrailsClick={onSeeTrailsClick}
            filterConfigs={filterConfigs}
            onClear={
              !!filterCount
                ? () => {
                    applyDistanceAway(undefined, true);
                    onFiltersChange({});
                    onSortChange(defaultExploreServiceSort);
                    if (customSortOptions) {
                      dispatch(updateCustomSortValue(customSortOptions[0].value));
                    }
                    onTrailEngagementChanged([]);
                    dispatch(updateNaturalLanguageQuery(undefined));
                  }
                : undefined
            }
            trailResultCount={isLoading ? null : (searchResultsSummary?.count ?? 0)}
          />
        )}
        {activeModal === 'difficulty' && (
          <ModalStep
            onCloseRequest={onCloseRequest}
            title={<FormattedMessage defaultMessage="Difficulty levels" />}
            subtitle={
              <div className={styles.difficultySubtitle}>
                <Typography variant="text200" color="secondary" component="div">
                  <FormattedMessage
                    defaultMessage="Our team considers <a>many factors</a>, like distance, elevation gain, and trailgoer feedback, to assign a difficulty level to each trail."
                    values={{
                      a: chunks => (
                        <Link href={getHelpCenterUrl(languageRegionCode, 16596491196436)} target="_blank" variant="secondary">
                          {chunks}
                        </Link>
                      )
                    }}
                  />
                </Typography>
                <Typography variant="text200" color="secondary">
                  <FormattedMessage defaultMessage="These levels are meant to apply to a wide audience — your experience may differ." />
                </Typography>
              </div>
            }
          >
            <DifficultyInfoModalContent />
          </ModalStep>
        )}
        {activeModal === 'wheelchair' && (
          <ModalStep onCloseRequest={onCloseRequest} title={<FormattedMessage defaultMessage="Wheelchair friendly" />}>
            <WheelchairInfoModalContent />
          </ModalStep>
        )}
      </MultiStepModal>
      {distanceAwaySignUpModal}
      {distanceAwayUpsellModal}
    </>
  );
};

export default AllFiltersModal;
